import {
  CasesApi,
  type CreateAnimalCaseInput,
  type CreateAnimalCasesBatchInput,
} from "@/api/cases/CasesApi";
import { CaseRelationsApi } from "@/api/cases/CaseRelationsApi";
import {
  intakeReasonIdsFromQuery,
  intakeReasonsStatusFromQuery,
} from "@/api/cases/intakeReasonEdit";
import { queryKeys } from "@/api/queryKeys";
import { useAuth } from "@/auth/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useMyCases() {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.cases.my,
    queryFn: () => CasesApi.listMyCases(),
    enabled: !!user?.id,
    staleTime: 10_000,
  });
  return {
    cases: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useCaseDetail(caseId: string | undefined) {
  const { user } = useAuth();
  const enabled = !!user?.id && !!caseId;

  const caseQ = useQuery({
    queryKey: queryKeys.cases.detail(caseId ?? ""),
    queryFn: () => CasesApi.getCase(caseId!),
    enabled,
  });

  const reasonsQ = useQuery({
    queryKey: queryKeys.cases.intakeReasonIds(caseId ?? ""),
    queryFn: () => CasesApi.listCaseIntakeReasonIds(caseId!),
    enabled,
  });

  // null (noch nicht erfolgreich geladen) nicht mit [] maskieren.
  const intakeReasonIds = intakeReasonIdsFromQuery(
    reasonsQ.data,
    reasonsQ.isSuccess,
  );

  return {
    animalCase: caseQ.data ?? null,
    intakeReasonIds,
    intakeReasonsStatus: intakeReasonsStatusFromQuery(
      reasonsQ.isSuccess,
      reasonsQ.isError,
      reasonsQ.data,
    ),
    loading: caseQ.isLoading,
    error: caseQ.error,
    refetch: async () => {
      await Promise.all([caseQ.refetch(), reasonsQ.refetch()]);
    },
  };
}

export function useIntakeReasons(organizationId: string | undefined) {
  const q = useQuery({
    queryKey: queryKeys.cases.intakeCatalog(organizationId ?? ""),
    queryFn: () => CasesApi.listIntakeReasons(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  return {
    reasons: q.data ?? [],
    loading: q.isLoading,
  };
}

export function useCreateAnimalCase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAnimalCaseInput) => CasesApi.createCase(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.cases.my });
    },
  });
}

export function useCreateAnimalCasesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAnimalCasesBatchInput) =>
      CasesApi.createCasesBatch(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.cases.my });
    },
  });
}

export function useCaseRelations(caseId: string | undefined) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.cases.relations(caseId ?? ""),
    queryFn: () => CaseRelationsApi.listForCase(caseId!),
    enabled: !!user?.id && !!caseId,
  });
  return {
    relations: q.data ?? [],
    loading: q.isLoading,
    error: q.error,
    refetch: q.refetch,
  };
}

export function useSameIdentityCases(caseId: string | undefined) {
  const { user } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.cases.sameIdentity(caseId ?? ""),
    queryFn: () => CaseRelationsApi.listSameIdentity(caseId!),
    enabled: !!user?.id && !!caseId,
  });
  return {
    cases: q.data ?? [],
    loading: q.isLoading,
    refetch: q.refetch,
  };
}

export function useCaseRelationCapabilities(
  organizationId: string | undefined
) {
  const canSuspectQ = useQuery({
    queryKey: ["cases", "canSuspect", organizationId ?? ""],
    queryFn: () => CaseRelationsApi.canDocumentSuspicion(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  const isAdminQ = useQuery({
    queryKey: ["cases", "isOrgAdmin", organizationId ?? ""],
    queryFn: () => CaseRelationsApi.isOrgAdmin(organizationId!),
    enabled: !!organizationId,
    staleTime: 60_000,
  });
  return {
    canDocumentSuspicion: !!canSuspectQ.data,
    isOrgAdmin: !!isAdminQ.data,
  };
}
